import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getListing, selectListingById } from '../../store/slices/listings';
import { selectWebsiteId, selectWebsiteTheme } from '../../store/slices/website';

import CLFontIcon from '../../components/CLFontIcon';
import CLSeparator from '../../components/CLSeparator';
import CLButton from '../../components/CLButton';
import ListingParams from '../../components-shared/ListingParams';
import ListingPrice from '../../components-shared/ListingPrice';

import Gallery from './components/Gallery';
import Map from './components/Map';
import DialogApply from './components/DialogApply';
import DialogContact from './components/DialogContact';

import {
  Container,
  Grid2,
  FeaturesContainer,
  Feature,
  NotAvailable,
  Actions,
  BackIcon,
} from './styled';

const ListingDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [applyIsVisible, setApplyIsVisible] = useState(false);
  const [contactIsVisible, setContactIsVisible] = useState(false);

  const [features, setFeatures] = useState([]);
  const [utilities, setUtilities] = useState([]);

  const { id } = useParams();
  const { colors } = useSelector(selectWebsiteTheme);
  const websiteId = useSelector(selectWebsiteId);
  const listing = useSelector((state) => selectListingById(state, id));

  useEffect(() => {
    dispatch(getListing({
      website_id: websiteId,
      listing_id: id,
    }));
  }, [id, websiteId, dispatch]);

  useEffect(() => {
    if (!listing) {
      return;
    }

    const _features = [];
    const _utilities = [];

    listing.feature_groups.forEach((group) => {
      if (group.feature_type.code_name === 'utility') {
        _utilities.push(group);
      } else {
        _features.push(group);
      }
    });

    setFeatures(_features);
    setUtilities(_utilities);
  }, [listing]);

  const getFeatureIcon = (feature) => {
    switch (feature) {
    case 'kitchen':
      return 'kitchen';
    case 'flooring':
      return 'floors';
    case 'rooms':
      return 'room';
    case 'amenities':
      return 'amenities';
    case 'outdoor':
      return 'outdoors';
    case 'parking':
      return 'vehicle';
    case 'community':
      return 'community';

    default:
      return 'search-outline';
    }
  };

  if (!listing) {
    return null;
  }

  return (
    <Container>
      <Gallery images={listing.images} />
      <DialogApply
        websiteId={websiteId}
        listing={listing}
        isVisible={applyIsVisible}
        onClose={() => setApplyIsVisible(false)}
      />
      <DialogContact
        websiteId={websiteId}
        listing={listing}
        isVisible={contactIsVisible}
        onClose={() => setContactIsVisible(false)}
      />

      <Grid2>

        <div id="base">
          <h1>{listing.listing_property.display_name}</h1>
          <h2 style={{ marginBottom: '24px' }}>{listing.listing_property.list_sub_name}</h2>

          <ListingPrice marginBottom="24px" listing={listing} />

          {listing.description.split('\n').map((line, index) => <p key={index}>{line}</p>)}

          <CLSeparator />
        </div>

        <div id="features">
          {features.length ? (
            <>
              <h2>{t('features', 'Features')}</h2>
              <FeaturesContainer>
                {features.map((feature) => (
                  <Feature key={feature.feature_group_id}>
                    <h3>
                      <CLFontIcon icon={getFeatureIcon(feature.feature_type.code_name)} />
                      {feature.feature_type.name}
                    </h3>
                    <ul>
                      {feature.feature_group_choices.map((choice) => (choice.is_on ? (
                        <li
                          key={choice.feature_choice_id}
                        >
                          {choice.feature_option.name}
                        </li>
                      ) : null
                      ))}
                    </ul>
                  </Feature>
                ))}
              </FeaturesContainer>

              <CLSeparator />
            </>
          ) : null}

          {listing.policy ? (
            <>
              <h2>{t('policies', 'Policies')}</h2>
              <FeaturesContainer>
                <Feature>
                  <h3>
                    <CLFontIcon icon="pet-dog" />
                    {t('dog_policy', 'Dog policy')}
                  </h3>
                  <ul>
                    <li>{listing.policy.dog_policy.name}</li>
                  </ul>
                </Feature>
                <Feature>
                  <h3>
                    <CLFontIcon icon="pet-cat" />
                    {t('cat_policy', 'Cat policy')}
                  </h3>
                  <ul>
                    <li>{listing.policy.cat_policy.name}</li>
                  </ul>
                </Feature>
                <Feature>
                  <h3>
                    <CLFontIcon icon="smoking" />
                    {t('smoking_policy', 'Smoking policy')}
                  </h3>
                  <ul>
                    <li>{listing.policy.smoking_policy.name}</li>
                  </ul>
                </Feature>
              </FeaturesContainer>

              <CLSeparator />
            </>
          ) : null}

          {utilities.length ? (
            <>
              <h2>{t('utilities', 'Utilities')}</h2>
              <FeaturesContainer>
                {utilities.map((utility) => (
                  <Feature key={utility.feature_group_id}>
                    <h3>
                      <CLFontIcon icon="params" />
                      {utility.feature_type.name}
                    </h3>
                    <ul>
                      {utility.feature_group_choices.map(
                        (choice) => (choice.is_on ? (
                          <li
                            key={choice.feature_choice_id}
                          >
                            {choice.feature_option.name}
                          </li>
                        ) : null),
                      )}
                    </ul>
                  </Feature>
                ))}
              </FeaturesContainer>
            </>
          ) : null}

        </div>

        <div id="params">
          {!listing.is_available ? (
            <NotAvailable
              color={colors[0].color}
              bgColor={colors[7].color}
            >
              <CLFontIcon icon="blocked" />
              <span>{t('not_available', 'Not available')}</span>
            </NotAvailable>
          ) : (
            <Actions>
              {listing.is_allow_leads && (
                <CLButton onClick={() => setContactIsVisible(true)}>
                  {t('contact', 'Contact')}
                </CLButton>
              )}
              {listing.is_display_contact && (
                <CLButton
                  primary
                  onClick={() => setApplyIsVisible(true)}
                >
                  {t('apply', 'Apply')}
                </CLButton>
              )}

            </Actions>
          )}

          <ListingParams listing={listing} />

          <FeaturesContainer>
            {listing.available_date && (
              <Feature>
                <h3>{t('availability', 'Availability')}</h3>
                <ul>
                  <li>{new Date(listing.available_date).toLocaleDateString()}</li>
                </ul>
              </Feature>
            )}

            {listing.neighborhood && (
              <Feature>
                <h3>{t('neighborhood', 'Neighborhood')}</h3>
                <ul>
                  <li>{listing.neighborhood}</li>
                </ul>
              </Feature>
            )}

            <Feature>
              <h3>{t('section_eight', 'Section 8')}</h3>
              <ul>
                <li>{listing.is_section_eight ? t('yes', 'Yes') : t('no', 'No')}</li>
              </ul>
            </Feature>
          </FeaturesContainer>

          <Map address={listing.listing_property?.address} />
        </div>

      </Grid2>

      <CLButton style={{ marginBottom: 24 }} primary onClick={() => navigate(-1)}>
        <BackIcon />
        {t('go_back', 'Go Back')}
      </CLButton>
    </Container>
  );
};

export default ListingDetails;
